import { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { IFavoritesDTO } from "../../Models/DTOs/IFavoritesDTO";
import { IListFavoritesParams, ListFavorites } from "../../Api/User";
import { UserAuthenticationContext } from "../../Context/UserAuthenticationContext";

function useFetchUserFavoriteContent(userId?: string, params?: IListFavoritesParams) {
    const auth = useContext(UserAuthenticationContext);
    const authRef = useRef(auth);
    authRef.current = auth;
    const [favorites, setFavorites] = useState<IFavoritesDTO | null>(null);
    const [favoritesLoading, setFavoritesLoading] = useState(false);
    const [favoritesError, setFavoritesError] = useState("");

    useEffect(() => {
        const abort = new AbortController();
        setFavorites(null);
        setFavoritesError("");
        if (!userId || authRef.current.authInitializing) {
            setFavoritesLoading(!!userId);
            return () => abort.abort();
        }
        setFavoritesLoading(true);
        void (async () => {
            try {
                const currentAuth = authRef.current;
                let token = await currentAuth.getValidAccessToken();
                if (abort.signal.aborted) return;
                if (!token) throw new Error("Sign in to load favorites.");
                let res = await ListFavorites(userId, abort, params, token);
                if (axios.isAxiosError(res) && res.response?.status === 401) {
                    const refreshed = await currentAuth.refreshTokens();
                    token = refreshed ? await currentAuth.getValidAccessToken() : null;
                    if (abort.signal.aborted) return;
                    if (!token) throw res;
                    res = await ListFavorites(userId, abort, params, token);
                }
                if (abort.signal.aborted) return;
                if (axios.isAxiosError(res)) throw res;
                setFavorites(res);
            } catch {
                if (!abort.signal.aborted) setFavoritesError("Failed to fetch favorites.");
            } finally {
                if (!abort.signal.aborted) setFavoritesLoading(false);
            }
        })();
        return () => abort.abort();
    }, [userId, auth.authInitializing, auth.authSessionVersion,
        params?.take, params?.skip, params?.publishedOnly, params?.type, params?.order]);
    return { favorites, favoritesLoading, favoritesError };
}
export default useFetchUserFavoriteContent;
